import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit,HostListener } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

import { PageEvent, PaginationMetadata, DestroyableBase } from '../../../../core';

import { Page } from './page';

/**
 * Paginator component.
 */
@Component({
  selector: 'arb-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent extends DestroyableBase implements OnInit {

  private maxPagesCount = 10;
  private currentPageIndex: number;
  private firstPageIndex = 1;
  private lastPageIndex: number;
  private isFirstPage: boolean;
  private isLastPage: boolean;
  private itemsTotalCount: number;
  

  /**
   * Pages.
   */
  public readonly pages$ = new BehaviorSubject<Page[]>([]);
  public isMobileView: boolean = true;
  public windowWidth;
  constructor()
  {
    super();
    this.windowWidth = window.innerWidth;
    this.isMobileView = this.windowWidth < 1150 ? true : false;
  }
  /**
   * Meta data.
   */
  @Input()
  public metadata$: Observable<PaginationMetadata>;
 @HostListener("window:resize", ["$event"]) toggleMobileView(windowEvent) {
    this.isMobileView = windowEvent.target.innerWidth < 1150 ? true : false;
  }
  /**
   * Emits clicked page index.
   */
  @Output()
  public pageChange = new EventEmitter<PageEvent>();

  /**
   * @inheritdoc
   */
  public ngOnInit(): void {
    if (typeof this.metadata$ === 'undefined') {
      throw new Error('Metadata is not provided');
    }
    this.listenMetadataChanging();
  }

  private listenMetadataChanging(): void {
    this.metadata$
      .pipe(
        tap(value => {
          this.isFirstPage = value.isFirstPage;
          this.isLastPage = value.isLastPage;
          this.currentPageIndex = value.page;
          this.lastPageIndex = value.totalPages;
          this.itemsTotalCount = value.totalCount;
          const allPages = this.generateAllPages(value);
          this.pages$.next(this.generatePages(allPages, value.page));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private generateAllPages(metadata: PaginationMetadata): Page[] {
    const pages: Page[] = [];
    for (let i = 1; i <= metadata.totalPages; i++) {
      pages.push(new Page(i, i === metadata.page));
    }
    return pages;
  }

  private generatePages(allPages: Page[], currentPageIndex: number): Page[] {
    const offset = Math.round(this.maxPagesCount / 2);
    const leftPages = this.getLeftPages(allPages, currentPageIndex, offset);
    const rightPages = this.getRightPages(allPages, currentPageIndex, offset);
    const result = [...leftPages, ...rightPages];
    return this.fillEmptySpaces(result, allPages);
  }

  private getLeftPages(allPages: Page[], index: number, offset: number): Page[] {
    const pages: Page[] = [];
    for (let i = index - 1, j = 0; j < offset; i--, j++) {
      if (!allPages[i]) {
        break;
      }
      pages.unshift(allPages[i]);
    }
    return pages;
  }

  private getRightPages(allPages: Page[], index: number, offset: number): Page[] {
    const pages: Page[] = [];
    for (let i = index, j = 0; j < offset && i < allPages.length; i++, j++) {
      if (!allPages[i]) {
        break;
      }
      pages.push(allPages[i]);
    }
    return pages;
  }

  /**
   * When `leftPages` is equal to `[1, 2, 3, 4, 5]` but `rightPages` is empty or vice versa
   * Need to fill array with pages while its length less than `maxPagesCount`.
   * @param pages Pages.
   * @param allPages All pages.
   */
  private fillEmptySpaces(pages: Page[], allPages: Page[]): Page[] {
    if (pages.length > 0) {
      if (pages[0].index === 1) {
        /* If pages is equal to [1, 2, 3, 4, 5] */
        for (let i = pages.length; i < this.maxPagesCount && i < this.lastPageIndex; i++) {
          pages.push(allPages[i]);
        }
      } else {
        /* If pages is equal to [46, 47, 48, 49, 50] (for example). */
        const firstPages = allPages.filter(item => item.index < pages[0].index);
        pages = [...firstPages.slice(0, this.maxPagesCount - pages.length), ...pages];
      }
    }
    return pages;
  }

  /**
   * Go to page.
   * @param pageIndex Page index.
   */
  public goTo(pageIndex: number): void {
    const previousPageIndex = this.currentPageIndex;
    this.currentPageIndex = pageIndex;
    this.emitPageEvent(previousPageIndex);
  }

  /**
   * Go to previous page.
   */
  public prev(): void {
    const previousPageIndex = this.currentPageIndex;
    this.currentPageIndex--;
    this.emitPageEvent(previousPageIndex);
  }

  /**
   * Go to next page.
   */
  public next(): void {
    const previousPageIndex = this.currentPageIndex;
    this.currentPageIndex++;
    this.emitPageEvent(previousPageIndex);
  }

  /**
   * Go to first page.
   */
  public first(): void {
    const previousPageIndex = this.currentPageIndex;
    this.currentPageIndex = this.firstPageIndex;
    this.emitPageEvent(previousPageIndex);
  }

  /**
   * Go to last page.
   */
  public last(): void {
    const previousPageIndex = this.currentPageIndex;
    this.currentPageIndex = this.lastPageIndex;
    this.emitPageEvent(previousPageIndex);
  }

  private emitPageEvent(previousPageIndex: number): void {
    if (previousPageIndex !== this.currentPageIndex) {
      this.pageChange.emit({
        pageIndex: this.currentPageIndex,
        previousPageIndex,
        length: this.itemsTotalCount,
      });
    }
  }

  /**
   * Next page disabled.
   */
  public get nextPageDisabled(): boolean {
    return this.isLastPage || this.currentPageIndex === this.lastPageIndex;
  }

  /**
   * First page disabled.
   */
  public get prevPageDisabled(): boolean {
    return this.isFirstPage || this.currentPageIndex === this.firstPageIndex;
  }

}
